import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Dialog } from "../me/index.js";
import { useDialogWege } from "./dialogWege.js";
import { RueckfrageGastgeber } from "./VerwerfenRueckfrage.js";
import "./Modal.css";
/** Die vier Stufen in Pixeln des Entwurfs — `me-dialog` rechnet sie selbst in rem um. */
const BREITE_PX = {
    standard: 480,
    mittel: 640,
    breit: 900,
    weit: 1200,
};
/**
 * Steht dieses Modal im Inhalt eines anderen? Dann ist es ein Subdialog und wird per Portal
 * an `document.body` gezeichnet (Review Aufgabe 1, K1/K2): Die Eltern-Maske bekommt von
 * me-dialog `data-neben`/`data-hinten` (`pointer-events: none`) bzw. am Telefon
 * `data-versteckt` (`display: none`), und beides erbte sich über den Flat Tree in einen
 * Subdialog in ihrem Inhalt — nicht zu treffen, am Telefon unsichtbar bei gesperrter Seite.
 * Masken der obersten Ebene bleiben, wo sie stehen (CSS-Kontext ihres Bereichs).
 */
const ImModal = createContext(null);
/**
 * Passen Eltern-Maske und Subdialog NEBENEINANDER ins Fenster? Sonst `unter="stapel"`.
 *
 * Seitlich (Entscheid Dirk 14) legt me-dialog den Subdialog links neben die Eltern-Maske und
 * hält dafür deren volle Breite frei. Bei einer weiten Maske (1200) und einem Subdialog
 * (480) auf einem 1280er Fenster läge der Subdialog dann ausserhalb des Bildes (E2E
 * beleg-pdf: „element is outside of the viewport"). Das Element weicht nicht selbst aus
 * (Lücke an me-design gemeldet); bis dahin entscheidet die Hülle beim Öffnen. Die Breiten
 * sind Pixel des Entwurfs auf 13px-Grundschrift — umgerechnet mit der Grundschrift der
 * Seite, wie das Element es in rem tut.
 */
/**
 * Der zuletzt mit Maus oder Finger angeklickte bedienbare Knopf/Verweis — der Auslöser, wenn
 * der Browser ihn beim Klick NICHT fokussiert (Safari/WebKit, Befund 13 an me-design). Ein
 * Horcher in der Einfangphase am Dokument, einmal angelegt; er liest nur.
 */
let letzterKlick = null;
let klickHorcherAngelegt = false;
const AUSLOESER = "button, a[href], [role=button], [role=menuitem], [tabindex]";
function klickHorcherAnlegen() {
    if (klickHorcherAngelegt || typeof document === "undefined")
        return;
    klickHorcherAngelegt = true;
    document.addEventListener("click", (ereignis) => {
        const ziel = ereignis.target instanceof Element
            ? ereignis.target.closest(AUSLOESER) : null;
        letzterKlick = ziel === null ? null : { ziel, zeit: Date.now() };
    }, true);
}
/**
 * Wer die Maske geöffnet hat: das fokussierte Element — oder, wenn der Fokus auf <body>
 * steht, der eben (≤ 1 s) angeklickte Knopf (WebKit fokussiert Knöpfe beim Klick nicht).
 */
function ausloeserErmitteln() {
    if (typeof document === "undefined")
        return null;
    const aktiv = document.activeElement;
    if (aktiv instanceof HTMLElement && aktiv !== document.body)
        return aktiv;
    if (letzterKlick !== null && Date.now() - letzterKlick.zeit <= 1000
        && letzterKlick.ziel.isConnected)
        return letzterKlick.ziel;
    return null;
}
function passtNebeneinander(elternPx, eigenePx) {
    if (typeof window === "undefined")
        return true;
    const grund = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
    return (elternPx + eigenePx) * (grund / 13) <= window.innerWidth;
}
export function Modal(p) {
    const eltern = useContext(ImModal);
    const breitePx = BREITE_PX[p.breite ?? "standard"];
    const anker = useRef(null);
    const steuerung = useRef(null);
    /*
     * Die Arbeitsteilung am Riegel, damit sie nicht geraten werden muss:
     *
     *  - **Der RAHMEN hält die Taste** (und das Kreuz im Kopf): Beide melden `me-abbrechen`,
     *    und hier wird JEDES `me-abbrechen` abgefangen (`preventDefault`) — das Element
     *    schliesst nie von selbst. Geschlossen wird, indem der Aufrufer das Modal aushängt;
     *    so bleiben React-Zustand und Dialog nie uneins. Ist der Ausgang gesperrt, geht der
     *    Rückruf gar nicht erst los (Fixrunde 1, 05.09.2026: ein Riegel allein am Knopf
     *    liesse die Tastatur daran vorbei).
     *  - **Die FUSSLEISTE hält den Knopf** (`ModalFuss`, dieselbe Angabe): Ein gesperrter
     *    Knopf löst gar nicht erst aus.
     *  - **Der Aufrufer hält seinen eigenen Weg.** Die `abbrechen()`-Funktionen prüfen den
     *    Arbeitszustand weiterhin selbst — für einen Knopf IM Panelinhalt („Anderen Kunden
     *    wählen"), den weder Rahmen noch Fußleiste kennen.
     */
    useDialogWege(anker, {
        beiAbbrechen: (ereignis) => {
            ereignis.preventDefault();
            if (p.abbrechenGesperrt === true)
                return;
            p.onAbbrechen();
        },
        /*
         * Review Aufgabe 1, W1: Ein natives `cancel` ohne Nutzeraktivierung (Escape mit Fokus
         * auf <body>, Android-Zurück — CloseWatcher-Regeln) lässt sich nicht verhindern; der
         * Dialog geht dann am Riegel und an der Rückfrage vorbei zu. Die Maske ist aber noch
         * eingehängt — geschlossen wird bei uns NUR durch Aushängen (dann hängt dieser Horcher
         * schon nicht mehr). Also: sofort wieder öffnen und, wenn nicht gesperrt, den
         * Abbrechen-Weg gehen (mit Rückfrage, wenn etwas geändert ist).
         */
        beiGeschlossen: () => {
            steuerung.current?.oeffnen();
            if (p.abbrechenGesperrt === true)
                return;
            p.onAbbrechen();
        },
    });
    /*
     * Im selben Commit öffnen, in dem die Maske eingehängt wird (Layout-Effekt): Das
     * kontrollierte `offen` der Hülle öffnet erst im späteren Effekt — bis dahin stünde ein
     * eingehängter, aber geschlossener Dialog im Dokument, dessen Felder schon da sind und
     * dessen Rahmen fehlt. `offen` bleibt trotzdem gesetzt; es findet den Dialog dann offen.
     * Nach `useDialogWege`: Die Riegel hängen, bevor der Dialog aufgeht.
     */
    useLayoutEffect(() => { steuerung.current?.oeffnen(); }, []);
    /*
     * Fokus-Rückgabe an den Auslöser (ME-Etappe 3, Aufgabe 8; Befund 13 an me-design): Beim
     * ersten Zeichnen merkt sich die Hülle, wer sie geöffnet hat (`ausloeserErmitteln`), und
     * gibt ihm den Fokus beim Aushängen ausdrücklich zurück — sonst fiele er in WebKit auf
     * <body>, hinter eine offene Eltern-Maske. Nur, wenn der Fokus danach VERLOREN ist
     * (<body>): Hat inzwischen jemand anderes ihn gesetzt — der Sprung zum ersten Fehler nach
     * einer 422 etwa —, bleibt er dort. Und nur, wenn der Auslöser noch im Dokument steht.
     */
    const [ausloeser] = useState(() => { klickHorcherAnlegen(); return ausloeserErmitteln(); });
    useLayoutEffect(() => () => {
        if (ausloeser === null)
            return;
        queueMicrotask(() => {
            const aktiv = document.activeElement;
            if (aktiv !== null && aktiv !== document.body)
                return;
            if (ausloeser.isConnected)
                ausloeser.focus();
        });
    }, [ausloeser]);
    /*
     * `strikt`: Ein Klick auf den Schleier schliesst NICHT (das Element prüft das selbst).
     * `offen` bleibt fest auf true — das Modal ist offen, solange es eingehängt ist.
     * Die Klasse `ki-modal` am Host ist nur Kennzeichen (die Einklapp-Regel der Verwaltung
     * nimmt ihn aus, Verwaltung.css); die Optik kommt vom Element.
     * `aria-label` = Titel, zusätzlich zum `aria-labelledby` des Elements: Das Element reicht
     * es an seine Ebene durch. Nicht jede Namensberechnung folgt dem Verweis auf die
     * Überschrift im Schatten samt Slot (Playwright fand den Dialog namenlos); bei
     * gefülltem Titel sticht `aria-labelledby` das Label ohnehin aus.
     */
    const dialog = (_jsx(ImModal.Provider, { value: { breitePx }, children: _jsxs(Dialog, { ref: steuerung, offen: true, strikt: true, lage: "rechts", breite: breitePx, unter: p.gestapelt === true
                || (eltern !== null && !passtNebeneinander(eltern.breitePx, breitePx))
                ? "stapel" : "seitlich", className: "ki-modal", "aria-label": p.titel, titel: _jsx("span", { ref: anker, children: p.titel }), children: [p.children, _jsx(RueckfrageGastgeber, {})] }) }));
    return eltern !== null ? createPortal(dialog, document.body) : dialog;
}
/**
 * Die Fußleiste eines strikten Modals — Speichern links, Abbrechen rechts, in jeder Maske
 * dieselbe Reihenfolge und dieselben Wörter.
 *
 * Sie steht hier und nicht in jeder Maske abgeschrieben, weil sie die Hausregel TRÄGT:
 * Solange jede Maske ihre zwei Knöpfe selbst zeichnete, konnte eine davon ein drittes
 * „Schliessen" dazustellen.
 */
export function ModalFuss(p) {
    /*
     * `slot="fuss"`: Der Fuss liegt als Kind von `Modal` im Inhalt, und `me-dialog` verteilt
     * ihn in seine Fußzeile (klebend, nur der Inhalt rollt). Dafür muss `ModalFuss`
     * UNMITTELBARES Kind von `Modal` sein — so steht er in allen Masken. `display: contents`
     * macht die zwei Knöpfe selbst zu den Kindern der Knopfzeile des Elements.
     */
    return (_jsxs("div", { slot: "fuss", className: "ki-modal__aktionen", style: { display: "contents" }, children: [_jsx("button", { type: "button", className: "ki-modal__primaer", disabled: p.speichernGesperrt === true, ...(p.speichernLabel === undefined ? {} : { "aria-label": p.speichernLabel }), onClick: p.onSpeichern, children: p.speichernText }), p.nurSchliessen !== true && (_jsx("button", { type: "button", className: "ki-modal__knopf", disabled: p.abbrechenGesperrt === true, onClick: p.onAbbrechen, children: "Abbrechen" }))] }));
}
// Die Rückfrage vor dem Abbrechen steht seit ME-Etappe 3 als Subdialog in
// VerwerfenRueckfrage.tsx; der Name bleibt hier erreichbar.
export { darfAbbrechen } from "./VerwerfenRueckfrage.js";
//# sourceMappingURL=Modal.js.map